import { MoreHorizontal, MessageSquare, Mail } from "lucide-react";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useNavigate } from "react-router-dom";

import { Project } from "@/hooks/useProjects";

// Re-export Project type for backwards compatibility
export type { Project };

interface ProjectCardProps {
  project: Project;
  onRename?: (id: string) => void;
  onArchive?: (id: string) => void;
  onRestore?: (id: string) => void;
  onDelete?: (id: string) => void;
}

const formatRelativeTime = (date: Date): string => {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
};

const ProjectCard = ({
  project,
  onRename,
  onArchive,
  onRestore,
  onDelete,
}: ProjectCardProps) => {
  const navigate = useNavigate();
  const isArchived = project.status === "archived";

  const handleCardClick = () => {
    if (!isArchived) {
      navigate(`/project/${project.id}`);
    }
  };

  const thumbnailInner = (
    <AspectRatio ratio={16 / 9}>
      <div className="flex h-full w-full items-center justify-center bg-foreground/5">
        {project.thumbnailUrl ? (
          <img
            src={project.thumbnailUrl}
            alt={project.name}
            className="h-full w-full object-cover"
          />
        ) : (
          <Mail className="h-8 w-8 text-foreground/20" />
        )}
      </div>
    </AspectRatio>
  );

  const dropdownMenu = (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button 
          onClick={(e) => e.stopPropagation()}
          className="absolute right-2 top-2 flex h-8 w-8 items-center justify-center rounded-lg bg-white ring-2 ring-foreground opacity-0 transition-opacity group-hover:opacity-100 hover:bg-white/90"
        >
          <MoreHorizontal className="h-4 w-4 text-foreground" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-40">
        {isArchived ? (
          <>
            <DropdownMenuItem
              className="cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                onRestore?.(project.id);
              }}
            >
              Restore
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="cursor-pointer text-destructive focus:text-destructive"
              onClick={(e) => {
                e.stopPropagation();
                onDelete?.(project.id);
              }}
            >
              Delete
            </DropdownMenuItem>
          </>
        ) : (
          <>
            <DropdownMenuItem
              className="cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                onRename?.(project.id);
              }}
            >
              Rename
            </DropdownMenuItem>
            <DropdownMenuItem
              className="cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                onArchive?.(project.id);
              }}
            >
              Archive
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="cursor-pointer text-destructive focus:text-destructive"
              onClick={(e) => {
                e.stopPropagation();
                onDelete?.(project.id);
              }}
            >
              Delete
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <div
      onClick={handleCardClick}
      className={`group relative flex flex-col rounded-2xl bg-background overflow-hidden transition-all duration-200 ${
        isArchived 
          ? "border border-foreground/10 opacity-50 grayscale-[30%] hover:border-foreground/20 cursor-default" 
          : "border border-foreground/10 hover:border-2 hover:border-accent hover:shadow-sm hover:scale-[1.02] cursor-pointer"
      }`}
    >
      {/* Thumbnail - with tooltip for archived */}
      <div className="relative">
        {thumbnailInner}
        {dropdownMenu}
      </div>

      {/* Card Content */}
      <div className="flex flex-1 flex-col p-4">
        <h3 className="font-medium text-foreground truncate">{project.name}</h3>

        <div className="mt-2 flex items-center justify-between">
          {isArchived ? (
            <Badge
              variant="secondary"
              className="bg-foreground text-background hover:bg-foreground"
            >
              Archived
            </Badge>
          ) : (
            <span className="text-sm text-muted-foreground">
              {formatRelativeTime(project.updatedAt)}
            </span>
          )}

          <div className="flex items-center gap-1 text-muted-foreground">
            <MessageSquare className="h-3.5 w-3.5" />
            <span className="text-xs">{project.commentCount}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectCard;
